import { Link } from "react-router-dom";
import { Star, Heart, Truck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export function ProductCard({ product }) {
  const { format } = useCurrency();
  const { add } = useCart();
  const { user } = useAuth();
  const out = product.stock <= 0 || product.status === "out_of_stock";
  const discount = product.compare_at_price > product.selling_price
    ? Math.round((1 - product.selling_price / product.compare_at_price) * 100) : 0;
  const hasVariants = (product.variants || []).length > 1;

  const badge = product.on_sale ? ["FLASH DEAL", "bg-[#E23744]"]
    : (product.sold_count > 1500 ? ["BEST SELLER", "bg-[#FF6A1A]"]
    : (product.rating >= 4.8 ? ["TOP RATED", "bg-emerald-600"] : null));

  const wish = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to save items"); return; }
    try { await api.post("/wishlist", { product_id: product.id }); toast.success("Saved to wishlist"); }
    catch { /* noop */ }
  };

  return (
    <Link to={`/product/${product.slug}`}
      className="group bg-white rounded-md border border-zinc-200 hover:shadow-lg hover:border-zinc-300 transition-all flex flex-col overflow-hidden"
      data-testid={`product-card-${product.slug}`}>
      <div className="relative aspect-square bg-zinc-50 overflow-hidden">
        <img src={(product.images || [])[0]} alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
        {discount > 0 && (
          <span className="absolute top-1.5 left-1.5 bg-[#E23744] text-white text-[11px] font-bold px-1.5 py-0.5 rounded">-{discount}%</span>
        )}
        {badge && <span className={`absolute top-1.5 ${discount > 0 ? "left-12" : "left-1.5"} ${badge[1]} text-white text-[9px] font-bold px-1.5 py-0.5 rounded tracking-wide`}>{badge[0]}</span>}
        <button onClick={wish} className="absolute top-1.5 right-1.5 bg-white/90 hover:bg-white p-1.5 rounded-full shadow-sm" data-testid={`wish-${product.slug}`}>
          <Heart size={14} className="text-zinc-600" />
        </button>
        {out && <div className="absolute inset-0 bg-white/60 flex items-center justify-center"><span className="bg-zinc-900 text-white text-xs px-3 py-1 rounded-full">Sold Out</span></div>}
      </div>
      <div className="p-2 flex flex-col flex-1">
        <p className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold">{product.brand}</p>
        <h3 className="text-[13px] leading-snug text-zinc-800 line-clamp-2 min-h-[34px] group-hover:text-[#FF6A1A]">{product.title}</h3>
        <div className="flex items-center gap-1 mt-1">
          <div className="flex items-center gap-0.5 bg-[#FFF3E6] px-1 rounded">
            <Star size={10} className="fill-[#FFA200] text-[#FFA200]" />
            <span className="text-[10px] font-semibold text-zinc-700">{product.rating || 0}</span>
          </div>
          <span className="text-[10px] text-zinc-400">({product.review_count || 0})</span>
          {product.sold_count > 0 && <span className="text-[10px] text-zinc-400">· {product.sold_count > 999 ? (product.sold_count / 1000).toFixed(1) + "k" : product.sold_count} sold</span>}
        </div>
        <div className="mt-1 flex items-end gap-1.5">
          <span className="text-[15px] font-bold text-[#E23744]" data-testid={`price-${product.slug}`}>{format(product.selling_price)}</span>
          {discount > 0 && <span className="text-[11px] text-zinc-400 line-through">{format(product.compare_at_price)}</span>}
        </div>
        {product.free_shipping && (
          <span className="mt-1 inline-flex items-center gap-1 text-[10px] text-emerald-600 font-medium"><Truck size={11} />Free shipping</span>
        )}
        <button
          onClick={(e) => { e.preventDefault(); if (!out && !hasVariants) add(product, null, 1); }}
          className={`mt-2 w-full text-[12px] font-semibold py-1.5 rounded transition-colors ${out ? "bg-zinc-100 text-zinc-400" : "bg-[#FF6A1A] hover:bg-[#e85f12] text-white"}`}
          data-testid={`add-${product.slug}`}>
          {out ? "Sold Out" : hasVariants ? "Choose Options" : "Add to Cart"}
        </button>
      </div>
    </Link>
  );
}
